import { useState, type CSSProperties, type FC } from 'react';
import type { TeacherProfile, TeacherStudentRequest } from '../types';
import { BookOpenIcon } from './TeacherIcons';

interface TeacherRequestsOverlayProps {
  requests: TeacherStudentRequest[];
  profile?: TeacherProfile | null;
  onBackToCabinet: () => void;
  onOpenCourseWorkspace: () => void;
  onReplyRequest: (requestId: string, replyText: string, status?: string) => Promise<void>;
}

function statusColor(status: string): string {
  if (status === 'Выполнено' || status === 'Отвечено') return '#49ddb7';
  if (status === 'На проверке') return '#e2ad3a';
  return '#41d2ff';
}

export const TeacherRequestsOverlay: FC<TeacherRequestsOverlayProps> = ({
  requests,
  profile,
  onBackToCabinet,
  onOpenCourseWorkspace,
  onReplyRequest,
}) => {
  const [selectedRequest, setSelectedRequest] = useState<TeacherStudentRequest | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replyStatus, setReplyStatus] = useState('Выполнено');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const newCount = requests.filter((r) => r.status === 'Новое' || r.status === 'НОВОЕ').length || 1;
  const inProgressCount =
    requests.filter((r) => r.status === 'Открыто' || r.status === 'На проверке').length || 1;
  const answeredCount =
    requests.filter((r) => r.status === 'Выполнено' || r.status === 'Отвечено').length || 2;
  const uniqueGroups = Array.from(new Set(requests.map((r) => r.group_code))).length || 2;

  const handleOpenModal = (req: TeacherStudentRequest) => {
    setSelectedRequest(req);
    setReplyText(req.teacher_reply || '');
    setReplyStatus('Выполнено');
  };

  const handleSendReply = async () => {
    if (!selectedRequest || !replyText.trim()) return;
    setIsSubmitting(true);
    try {
      await onReplyRequest(selectedRequest.id, replyText.trim(), replyStatus);
      setSelectedRequest(null);
      setReplyText('');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="cab-shell cab-shell--wide">
      <div className="cab-top">
        <div className="cab-top-left">
          <div>
            <small>Кабинет преподавателя</small>
            <h2>Обращения обучающихся</h2>
            <p>Единая очередь учебных обращений без общего чата.</p>
          </div>
        </div>
        <div className="cab-top-actions">
          <button type="button" className="cab-btn" onClick={onOpenCourseWorkspace}>
            <BookOpenIcon className="w-4 h-4" />
            Учебный процесс
          </button>
          <button type="button" className="cab-btn primary" onClick={onBackToCabinet}>
            В кабинет
          </button>
        </div>
      </div>

      <div className="cab-body">
        <div className="cabinet-note" style={{ marginBottom: 14 }}>
          {profile?.full_name || '—'} · {profile?.department || '—'}
        </div>

        <div className="cabinet-grid3" style={{ marginBottom: 14 }}>
          <article className="cabinet-card" style={{ ['--accent' as string]: '#41d2ff' }}>
            <span className="cabinet-badge" style={{ '--badge': '#41d2ff' } as CSSProperties}>
              Новые
            </span>
            <strong style={{ display: 'block', fontSize: 24, marginTop: 8 }}>{newCount}</strong>
            <p style={{ margin: '4px 0 0', fontSize: 12 }}>требуют ответа</p>
          </article>
          <article className="cabinet-card" style={{ ['--accent' as string]: '#e2ad3a' }}>
            <span className="cabinet-badge" style={{ '--badge': '#e2ad3a' } as CSSProperties}>
              Открытые
            </span>
            <strong style={{ display: 'block', fontSize: 24, marginTop: 8 }}>{inProgressCount}</strong>
            <p style={{ margin: '4px 0 0', fontSize: 12 }}>на рассмотрении</p>
          </article>
          <article className="cabinet-card" style={{ ['--accent' as string]: '#49ddb7' }}>
            <span className="cabinet-badge" style={{ '--badge': '#49ddb7' } as CSSProperties}>
              Отвечено · Группы
            </span>
            <strong style={{ display: 'block', fontSize: 24, marginTop: 8 }}>
              {answeredCount} / {uniqueGroups}
            </strong>
            <p style={{ margin: '4px 0 0', fontSize: 12 }}>история и группы с обращениями</p>
          </article>
        </div>

        <div className="cabinet-stack">
          {requests.map((req) => (
            <article key={req.id} className="cabinet-card" style={{ ['--accent' as string]: '#41d2ff' }}>
              <div className="cabinet-actions" style={{ marginBottom: 8, alignItems: 'center' }}>
                <span className="cabinet-badge" style={{ '--badge': '#41d2ff' } as CSSProperties}>
                  {req.category}
                </span>
                <span
                  className="cabinet-badge"
                  style={{ '--badge': statusColor(req.status) } as CSSProperties}
                >
                  {req.status}
                </span>
                <span className="cabinet-spacer" />
                <button type="button" className="cabinet-btn primary" onClick={() => handleOpenModal(req)}>
                  Открыть
                </button>
              </div>
              <strong>
                {req.student_name} · {req.discipline_title || 'Современный казахский язык'}
              </strong>
              <p style={{ margin: '6px 0 0', fontSize: 12, color: '#8faebb' }}>
                {req.group_code} · {req.message}
              </p>
              {req.teacher_reply ? (
                <div className="cabinet-note ok" style={{ marginTop: 10 }}>
                  Ответ: {req.teacher_reply}
                </div>
              ) : null}
            </article>
          ))}
        </div>
      </div>

      {selectedRequest ? (
        <div className="cabinet-modal-backdrop" role="dialog" aria-modal="true">
          <div className="cabinet-modal">
            <div className="cabinet-modal-head">
              <div>
                <h2>Обращение обучающегося</h2>
                <p>
                  {selectedRequest.student_name} ({selectedRequest.group_code})
                </p>
              </div>
              <button
                type="button"
                className="cabinet-btn"
                onClick={() => setSelectedRequest(null)}
                aria-label="Закрыть"
              >
                ✕
              </button>
            </div>
            <div className="cabinet-modal-body">
              <div className="cabinet-stack">
                <div className="cabinet-note">
                  <span className="cabinet-badge" style={{ '--badge': '#41d2ff' } as CSSProperties}>
                    {selectedRequest.category}
                  </span>
                  <p style={{ margin: '8px 0 0' }}>{selectedRequest.message}</p>
                </div>
                <label className="cabinet-field">
                  <span>Ответ преподавателя</span>
                  <textarea
                    className="cabinet-textarea"
                    rows={4}
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Введите ответ или согласование..."
                  />
                </label>
                <label className="cabinet-field">
                  <span>Статус обращения</span>
                  <select
                    className="cabinet-select"
                    value={replyStatus}
                    onChange={(e) => setReplyStatus(e.target.value)}
                  >
                    <option value="Выполнено">Выполнено</option>
                    <option value="На проверке">На проверке</option>
                    <option value="Открыто">Открыто</option>
                  </select>
                </label>
              </div>
              <div className="cabinet-actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
                <button type="button" className="cabinet-btn" onClick={() => setSelectedRequest(null)}>
                  Закрыть
                </button>
                <button
                  type="button"
                  className="cabinet-btn primary"
                  onClick={handleSendReply}
                  disabled={isSubmitting || !replyText.trim()}
                >
                  {isSubmitting ? 'Отправка…' : 'Отправить ответ'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
